import React, { useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, useReducedMotion } from 'framer-motion';
import { ArrowLeftIcon, ArrowUpIcon, ArrowUpRightIcon } from 'lucide-react';
import { imprintDetails, legalSections, legalUpdated, modelCredits, type LegalSection } from '../data/legal';
import { focusIn } from '../utils/reveal';
import { useCurrentYear } from '../hooks/useCurrentYear';
const LINK = 'underline decoration-black/20 underline-offset-[0.2em] transition-colors duration-200 ease-out hover:decoration-black focus-visible:rounded-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-black';
const FOCUS = 'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-black';
/** Page gutters: roomy on phones (24px), the site's usual gutter on desktop */
const GUTTER = 'px-6 md:px-[calc(1.6*var(--vw))]';

/** Sections with a button in the switcher — credits stay on the page, just without a button */
const navSections = legalSections.filter(s => s.id !== 'credits');

/** Imprint, privacy, cookies and credits on one page; a section switcher jumps between them. */
export function Legal() {
  const reduce = useReducedMotion();
  const year = useCurrentYear();
  const {
    hash
  } = useLocation();
  const [active, setActive] = useState<LegalSection['id']>(legalSections[0].id);
  const tabsRef = useRef<HTMLDivElement>(null);

  // Open at the requested section (e.g. /legal#cookies from the footer), otherwise at the top
  useEffect(() => {
    const id = hash.replace('#', '');
    const el = id ? document.getElementById(id) : null;
    if (el) el.scrollIntoView({
      block: 'start'
    });else window.scrollTo(0, 0);
  }, [hash]);

  // Highlight the section currently being read
  useEffect(() => {
    const els = legalSections.map(s => document.getElementById(s.id)).filter((e): e is HTMLElement => !!e);
    const io = new IntersectionObserver(entries => {
      const hit = entries.find(e => e.isIntersecting);
      if (hit) setActive(hit.target.id as LegalSection['id']);
    }, {
      rootMargin: '-35% 0px -60% 0px'
    });
    els.forEach(e => io.observe(e));
    return () => io.disconnect();
  }, []);

  // Phones: keep the active tab in view inside the scrollable switcher
  useEffect(() => {
    const tab = tabsRef.current?.querySelector<HTMLElement>(`[data-tab="${active}"]`);
    tab?.scrollIntoView({
      block: 'nearest',
      inline: 'center',
      behavior: reduce ? 'auto' : 'smooth'
    });
  }, [active, reduce]);
  return <main className="min-h-screen w-full bg-white font-sans text-black" data-id="element-327" data-mp="2r8315wffuhr6">
      {/* Top bar */}
      <header className={`flex items-center justify-between py-4 md:py-6 ${GUTTER}`} data-id="element-328" data-mp="2w8h97eh1hrp7">
        <Link to="/" className={`inline-flex items-center gap-2 rounded-full text-[0.95rem] font-bold tracking-[-0.03em] transition-opacity duration-200 ease-out hover:opacity-50 ${FOCUS}`} data-id="element-329" data-mp="6jf19yyvg2lh">
          <ArrowLeftIcon className="h-4 w-4" aria-hidden="true" data-id="element-330" data-mp="lkvkuwlqaad8" />
          Back to Immersive Studios
        </Link>
        <Link to="/" aria-label="Immersive Studios home" className={`rounded-sm ${FOCUS}`} data-id="element-331" data-mp="gndjpsdyczfs">
          <img src="https://cdn.magicpatterns.com/uploads/oiFnxEA5Ln1uptx1c81gJo/Vector_(2).svg" alt="" className="h-8 w-auto md:h-10" data-id="element-332" data-mp="busr2u6s8sdu" />
        </Link>
      </header>

      {/* Title */}
      <div className={`pb-10 pt-[clamp(3.5rem,calc(8*var(--vw)),8rem)] md:pb-0 ${GUTTER}`} data-id="element-333" data-mp="2t2rh12qyn4z">
        <motion.h1 className="text-[clamp(3.5rem,calc(7.3*var(--vw)),8.75rem)] font-bold leading-[0.95] tracking-[-0.055em]" {...focusIn(0.05, reduce, {
        y: 14,
        blur: 14,
        duration: 1.1
      })} data-id="element-334" data-mp="3u3tfb0jbtvy5">
          Legal
        </motion.h1>
        <motion.p className="mt-5 max-w-[34rem] font-serif text-[1.25rem] font-medium leading-snug md:font-normal md:text-[1.5rem]" {...focusIn(0.15, reduce, {
        y: 8,
        blur: 8,
        duration: 1
      })} data-id="element-335" data-mp="1na93w1fat075">
          The fine print — imprint, privacy, cookies and credits, in plain language.
        </motion.p>
        <motion.p className="mt-4 text-[0.9rem] font-bold tracking-[-0.02em] text-black/40" {...focusIn(0.25, reduce, {
        y: 6,
        blur: 6,
        duration: 1
      })} data-id="element-336" data-mp="1vbghkzuw9s79">
          Last updated {legalUpdated}
        </motion.p>
      </div>

      {/* Phones: sticky, horizontally scrollable section switcher */}
      <nav aria-label="Legal sections" className="sticky top-0 z-20 border-y border-black/10 bg-white/90 backdrop-blur-md md:hidden" data-id="element-337" data-mp="17kb3ap4lr7z2">
        <div ref={tabsRef} className="flex gap-2 overflow-x-auto px-6 py-3 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden" data-id="element-338" data-mp="xp9b1kf15ps9">
          {navSections.map(s => {
          const on = active === s.id;
          return <a key={s.id} data-tab={s.id} href={`#${s.id}`} aria-current={on ? 'true' : undefined} className={`shrink-0 whitespace-nowrap rounded-full px-4 py-2 text-[0.9rem] font-bold tracking-[-0.02em] transition-colors duration-200 ease-out ${FOCUS} ${on ? 'bg-black text-white' : 'bg-black/[0.05] text-black/70'}`} data-id="element-339" data-mp="opmkxurr46ei">
                {s.title}
              </a>;
        })}
        </div>
      </nav>

      <div className={`pb-24 md:pb-32 md:pt-[clamp(4rem,calc(8*var(--vw)),8rem)] ${GUTTER}`} data-id="element-340" data-mp="sz5mslne5jy5">
        <div className="grid gap-12 md:grid-cols-[minmax(0,1fr)_minmax(0,2.4fr)] md:gap-[calc(4*var(--vw))]" data-id="element-341" data-mp="o142xyi55glm">
          {/* Desktop: section index sticks alongside the text */}
          <nav aria-label="Legal sections" className="hidden md:sticky md:top-8 md:block md:self-start" data-id="element-342" data-mp="xqf6z6q67v2n">
            <ul className="flex flex-col gap-2 text-[clamp(1rem,calc(1.3*var(--vw)),1.5rem)] font-bold tracking-[-0.03em]" data-id="element-343" data-mp="1bpx181m8k0je">
              {navSections.map(s => <li key={s.id} data-id="element-344" data-mp="16swrngq6gfnj">
                  <a href={`#${s.id}`} aria-current={active === s.id ? 'true' : undefined} className={`rounded-sm transition-opacity duration-200 ease-out hover:opacity-100 ${FOCUS} ${active === s.id ? 'opacity-100' : 'opacity-35'}`} data-id="element-345" data-mp="4dxmf9fh30fv">
                    {s.title}
                  </a>
                </li>)}
            </ul>
          </nav>

          <div className="max-w-[46rem]" data-id="element-346" data-mp="n6jedin9e6v6">
            {legalSections.map((section, i) => <SectionBody key={section.id} section={section} first={i === 0} data-id="element-347" data-mp="s5ct1eea83wx" data-mpi={i} />)}
          </div>
        </div>
      </div>

      {/* Closing strip */}
      <footer className={`bg-black py-14 text-white md:py-20 ${GUTTER}`} data-id="element-348" data-mp="112wt4k0eru5y">
        <p className="text-[clamp(2rem,calc(3.4*var(--vw)),3.75rem)] font-bold leading-[1] tracking-[-0.045em]" data-id="element-349" data-mp="2yu7v6wkwn2l5">Questions?</p>
        <a href="mailto:hey@immersivestudios.de" className="mt-3 inline-block rounded-sm text-[1.125rem] font-semibold tracking-[-0.02em] text-white/70 underline decoration-white/30 underline-offset-[0.2em] transition-colors duration-200 ease-out hover:text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white" data-id="element-350" data-mp="32w2vbxr3n9xc">
          hey@immersivestudios.de
        </a>
        <div className="mt-12 flex items-center justify-between border-t border-white/15 pt-6 text-[0.9rem] font-bold tracking-[-0.02em] text-white/40" data-id="element-351" data-mp="2hikl8dx243bi">
          <span data-id="element-352" data-mp="39cuxqf0ec32j">Immersive Studios © {year}</span>
          <a href="#top" onClick={e => {
          e.preventDefault();
          window.scrollTo({
            top: 0,
            behavior: reduce ? 'auto' : 'smooth'
          });
        }} className="inline-flex items-center gap-1.5 rounded-sm transition-colors duration-200 ease-out hover:text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white" data-id="element-353" data-mp="359y9o8s37er7">
            Back to top
            <ArrowUpIcon className="h-4 w-4" aria-hidden="true" data-id="element-354" data-mp="3owejpp02k5nv" />
          </a>
        </div>
      </footer>
    </main>;
}
function SectionBody({
  section,
  first
}: {
  section: LegalSection;
  first: boolean;
}) {
  return <section id={section.id} aria-labelledby={`${section.id}-title`}
  // Phones: offset for the sticky switcher; a hairline separates each section
  className={`scroll-mt-24 md:scroll-mt-8 ${first ? 'pt-10 md:pt-0' : 'mt-16 border-t border-black/10 pt-12 md:mt-[clamp(5rem,calc(8*var(--vw)),8rem)] md:border-t-0 md:pt-0'}`} data-id="element-355" data-mp="3f24ntxbx4p8e">
      <h2 id={`${section.id}-title`} className="text-[clamp(2.25rem,calc(3.4*var(--vw)),3.75rem)] font-bold leading-[1] tracking-[-0.045em]" data-id="element-356" data-mp="1s8emy8zcdmc">
        {section.title}
      </h2>
      {section.intro && <p className="mt-5 font-serif text-[1.2rem] font-medium leading-snug text-black/80 md:font-normal md:text-[1.5rem]" data-id="element-357" data-mp="uh5ivxgcw9w3">
          {section.intro}
        </p>}

      {section.id === 'imprint' && <dl className="mt-8 divide-y divide-black/10 rounded-2xl bg-black/[0.03] px-5 md:mt-10 md:rounded-none md:border-y md:border-black/10 md:bg-transparent md:px-0" data-id="element-358" data-mp="2jxhvn4wwlhni">
          {imprintDetails.map(d => <div key={d.label} className="grid gap-1 py-4 sm:grid-cols-[14rem_1fr] sm:gap-6" data-id="element-359" data-mp="2sql40ysoz4ba">
              <dt className="text-[0.85rem] font-bold tracking-[-0.01em] text-black/45" data-id="element-360" data-mp="2oqksblwz977x">{d.label}</dt>
              <dd className="break-words text-[1rem] font-semibold tracking-[-0.02em]" data-id="element-361" data-mp="210gq9izftfj1">
                {d.href ? <a href={d.href} className={LINK} {...d.href.startsWith('http') ? {
            target: '_blank',
            rel: 'noopener noreferrer'
          } : {}} data-id="element-362" data-mp="1w3ghh0b7dyo8">
                    {d.value}
                  </a> : d.value}
              </dd>
            </div>)}
        </dl>}

      {section.id === 'credits' && <ul className="mt-8 md:mt-10" data-id="element-363" data-mp="3gg5sfx7m6try">
          {modelCredits.map(c => <li key={c.workUrl} className="rounded-2xl bg-black/[0.03] p-5 md:rounded-none md:border-y md:border-black/10 md:bg-transparent md:px-0 md:py-6" data-id="element-364" data-mp="17ah9tx7s8q75">
              <p className="text-[0.85rem] font-bold tracking-[-0.01em] text-black/45" data-id="element-365" data-mp="12dh2liz75heg">3D model</p>
              <p className="mt-2 text-[1.0625rem] font-semibold leading-relaxed tracking-[-0.02em]" data-id="element-366" data-mp="xi1ruzwhk1m9">
                “
                <a href={c.workUrl} target="_blank" rel="noopener noreferrer" className={LINK} data-id="element-367" data-mp="ok97viftk5sj">
                  {c.work}
                </a>
                ” by{' '}
                <a href={c.authorUrl} target="_blank" rel="noopener noreferrer" className={LINK} data-id="element-368" data-mp="jmzmcdvjwe9z">
                  {c.author}
                </a>
                , licensed under{' '}
                <a href={c.licenseUrl} target="_blank" rel="noopener noreferrer" className={LINK} data-id="element-369" data-mp="alrnnup4hbfd">
                  {c.license}
                </a>
                <ArrowUpRightIcon className="ml-1 inline h-4 w-4 align-[-0.1em] text-black/40" aria-hidden="true" data-id="element-370" data-mp="24xg98fpv0c8q" />
              </p>
              <p className="mt-2 text-[0.95rem] leading-relaxed text-black/60" data-id="element-371" data-mp="3mv33gns4d696">{c.note}</p>
            </li>)}
        </ul>}

      {section.blocks.map((b, i) => <div key={i} className={b.heading ? 'mt-9' : 'mt-3'} data-id="element-372" data-mp="h2w3cr9av1y" data-mpi={i}>
          {b.heading && <h3 className="text-[1.125rem] font-bold tracking-[-0.03em] md:text-[1.25rem]" data-id="element-373" data-mp="4hda9cp7crrw">{b.heading}</h3>}
          {b.paragraphs?.map(p => <p key={p} className="mt-2.5 text-[0.975rem] leading-[1.7] text-black/70 md:text-[1rem]" data-id="element-374" data-mp="33ybdjvi2nsw5">
              {p}
            </p>)}
          {b.items && <ul className="mt-3 space-y-2 text-[0.975rem] leading-[1.6] text-black/70 md:text-[1rem]" data-id="element-375" data-mp="3cwdjmpgnxbqa">
              {b.items.map(it => <li key={it} className="relative pl-5 before:absolute before:left-0 before:top-[0.7em] before:h-[5px] before:w-[5px] before:rounded-full before:bg-black/25" data-id="element-376" data-mp="3huf3hclwxf2t">
                  {it}
                </li>)}
            </ul>}
        </div>)}
    </section>;
}